/**
 * Custom hook for checking organization capabilities
 * Used to conditionally render features based on enabled capabilities
 *
 * Coalesces all per-capability lookups for an org into a single bulk
 * GET /api/organizations/{orgId}/capabilities call (cached for CACHE_TTL).
 * This avoids the 9+ parallel single-capability requests that previously
 * exhausted the backend DB pool on app load.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/apiClient';
import { config as productConfig } from '@/src/config';

// ----------------------------------------------------------------------------
// Triveny shielding — frontend mirror of HEALTHCARE_ONLY_CAPABILITIES in
// app/routers/capabilities.py. Keeps the gate fail-safe even if the backend
// is unreachable or returns stale data: on Triveny, these capabilities are
// hard-coded to "disabled" before any network call.
//
// ⚠️  Drift caught by CI: this set is snapshotted in
//   hooks/__tests__/useCapabilities.policy.test.ts and the backend mirror
//   is snapshotted in app/tests/unit/test_triveny_capability_policy.py.
//   Updating this set requires updating BOTH snapshot files — the tests
//   fail if either repo's snapshot diverges from its hook/routers code.
// ----------------------------------------------------------------------------
export const HEALTHCARE_ONLY_CAPABILITIES = new Set<string>([
  // care_coordination
  'telehealth',
  'care_referrals',
  'network_directory',
  'care_plan_management',
  'service_authorization',
  'discharge_planning',
  'chw_supervision',
  'hub_management',
  'nemt_transportation',
  'housing_services',
  'clinical_social_handoff',
  'deep_integration',
  // healthcare-specific compliance
  'eligibility_verification',
  'ffs_transition', // TRUS-739
  'credentialing_management',
  'grievance_management',
  // healthcare-specific insights
  'quality_measures',
  'outcomes_tracking',
  // payer/CBO finance
  'funds_flow_management',
  'funding_opportunities', // TRUS-776: payer directory + Medicaid program matching
  // medicaid program-specific
  'medicaid_work_requirements',
  'medicaid_compliance',
]);

export const TRIVENY_DEFAULT_CAPABILITIES = new Set<string>([
  'member_management',
  'program_management',
  'analytics',
  'communications',
  'ai_assistance',
  'survey_feedback',
  'workflow_automation',
]);

/** Returns true if Triveny mode should deny this capability without an API call. */
function isShieldedOnTriveny(capabilityName: string): boolean {
  return productConfig.isTriveny && HEALTHCARE_ONLY_CAPABILITIES.has(capabilityName);
}

/** Returns true if Triveny mode should fast-path this as enabled without an API call. */
function isTrivenyDefault(capabilityName: string): boolean {
  return productConfig.isTriveny && TRIVENY_DEFAULT_CAPABILITIES.has(capabilityName);
}

interface CapabilityCheckResponse {
  capability_name: string;
  enabled: boolean;
  config: Record<string, any>;
}

interface BulkCapabilityEntry {
  capability_name: string;
  enabled: boolean;
  config: Record<string, any> | null;
}

// Simple in-memory cache to prevent redundant API calls
const capabilityCache = new Map<string, { enabled: boolean; config: Record<string, any>; timestamp: number; error?: boolean }>();
const CACHE_TTL = 60000; // 1 minute cache
const ERROR_CACHE_TTL = 10000; // 10 second cache for errors (to prevent spam but allow retry)

// In-flight bulk-fetch promises keyed by org id. Multiple hooks mounting at
// the same time share one promise instead of issuing parallel requests.
const bulkFetchInFlight = new Map<number, Promise<Map<string, { enabled: boolean; config: Record<string, any> }>>>();

/**
 * Fetch all capabilities for an org via the bulk endpoint, deduping concurrent
 * callers. Populates the per-capability cache so subsequent useCapability
 * lookups for the same org return synchronously from cache.
 */
const fetchAllCapabilitiesForOrg = (
  organizationId: number,
): Promise<Map<string, { enabled: boolean; config: Record<string, any> }>> => {
  const existing = bulkFetchInFlight.get(organizationId);
  if (existing) return existing;

  const promise = apiClient
    .get(`/api/organizations/${organizationId}/capabilities`, {
      headers: { 'X-CBO-ID': organizationId.toString() },
    })
    .then((response) => {
      const rows: BulkCapabilityEntry[] = Array.isArray(response.data) ? response.data : [];
      const map = new Map<string, { enabled: boolean; config: Record<string, any> }>();
      const now = Date.now();
      rows.forEach((row) => {
        const entry = { enabled: row.enabled, config: row.config || {} };
        map.set(row.capability_name, entry);
        capabilityCache.set(`${organizationId}:${row.capability_name}`, {
          ...entry,
          timestamp: now,
        });
      });
      return map;
    })
    .finally(() => {
      bulkFetchInFlight.delete(organizationId);
    });

  bulkFetchInFlight.set(organizationId, promise);
  return promise;
};

/**
 * Hook for checking if a specific capability is enabled for an organization
 */
export const useCapability = (organizationId: number | null, capabilityName: string) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [config, setConfig] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Track if we've already made a request for this org/capability combo
  const requestInFlightRef = useRef<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      setEnabled(false);
      return;
    }

    // Triveny shielding — fail-safe path for healthcare-only capabilities.
    // No API call, no 401 spam in the console, no race-condition window
    // where the UI might briefly show a healthcare feature before the
    // capability response lands.
    if (isShieldedOnTriveny(capabilityName)) {
      setEnabled(false);
      setConfig({});
      setLoading(false);
      setError(null);
      return;
    }
    // Triveny defaults — likewise short-circuit to enabled=true so the
    // B2B-native pages (Pipeline, Lead Builder, etc.) render immediately
    // on first load.
    if (isTrivenyDefault(capabilityName)) {
      setEnabled(true);
      setConfig({});
      setLoading(false);
      setError(null);
      return;
    }

    const cacheKey = `${organizationId}:${capabilityName}`;

    // Check cache first
    const cached = capabilityCache.get(cacheKey);
    const now = Date.now();
    const ttl = cached?.error ? ERROR_CACHE_TTL : CACHE_TTL;

    if (cached && (now - cached.timestamp) < ttl) {
      setEnabled(cached.enabled);
      setConfig(cached.config);
      setLoading(false);
      if (cached.error) {
        setError('Capability check failed (cached)');
      }
      return;
    }

    // Prevent duplicate in-flight requests
    if (requestInFlightRef.current === cacheKey) {
      return;
    }

    const checkCapability = async () => {
      requestInFlightRef.current = cacheKey;

      try {
        setLoading(true);
        setError(null);

        const all = await fetchAllCapabilitiesForOrg(organizationId);
        const entry = all.get(capabilityName) || { enabled: false, config: {} };

        setEnabled(entry.enabled);
        setConfig(entry.config);

        // Record the "missing" case too so we don't re-fetch on every render
        capabilityCache.set(cacheKey, {
          enabled: entry.enabled,
          config: entry.config,
          timestamp: Date.now(),
        });
      } catch (err: any) {
        console.error(`Failed to check capability ${capabilityName}:`, err);
        setError(err.response?.data?.detail || err.message || 'Failed to check capability');
        // Default to disabled on error
        setEnabled(false);
        setConfig({});

        // Cache the error state to prevent spam
        capabilityCache.set(cacheKey, {
          enabled: false,
          config: {},
          timestamp: Date.now(),
          error: true
        });
      } finally {
        setLoading(false);
        requestInFlightRef.current = null;
      }
    };

    checkCapability();
  }, [organizationId, capabilityName]);

  return { enabled, config, loading, error };
};

/**
 * Hook for checking multiple capabilities at once
 */
export const useCapabilities = (organizationId: number | null, capabilityNames: string[]) => {
  const [capabilities, setCapabilities] = useState<Record<string, boolean>>({});
  const [configs, setConfigs] = useState<Record<string, Record<string, any>>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs for stable callback references to prevent infinite re-render loops
  const capabilitiesRef = useRef<Record<string, boolean>>({});
  const configsRef = useRef<Record<string, Record<string, any>>>({});
  capabilitiesRef.current = capabilities;
  configsRef.current = configs;

  useEffect(() => {
    if (!organizationId || capabilityNames.length === 0) {
      setLoading(false);
      setCapabilities({});
      return;
    }

    const checkCapabilities = async () => {
      try {
        setLoading(true);
        setError(null);

        const all = await fetchAllCapabilitiesForOrg(organizationId);

        const capabilitiesMap: Record<string, boolean> = {};
        const configsMap: Record<string, Record<string, any>> = {};
        capabilityNames.forEach((name) => {
          const entry = all.get(name);
          capabilitiesMap[name] = entry?.enabled ?? false;
          configsMap[name] = entry?.config ?? {};
        });

        setCapabilities(capabilitiesMap);
        setConfigs(configsMap);
      } catch (err: any) {
        console.error('Failed to check capabilities:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to check capabilities');
        // Default all to disabled on error
        const disabledMap: Record<string, boolean> = {};
        capabilityNames.forEach(name => {
          disabledMap[name] = false;
        });
        setCapabilities(disabledMap);
        setConfigs({});
      } finally {
        setLoading(false);
      }
    };

    checkCapabilities();
  }, [organizationId, JSON.stringify(capabilityNames)]);

  // Use ref to prevent callback recreation on every state change
  const hasCapability = useCallback(
    (capabilityName: string): boolean => {
      return capabilitiesRef.current[capabilityName] || false;
    },
    [] // Empty deps - callback is stable, uses ref for current data
  );

  // Use ref to prevent callback recreation on every state change
  const getCapabilityConfig = useCallback(
    (capabilityName: string): Record<string, any> => {
      return configsRef.current[capabilityName] || {};
    },
    [] // Empty deps - callback is stable, uses ref for current data
  );

  return {
    capabilities,
    configs,
    hasCapability,
    getCapabilityConfig,
    loading,
    error
  };
};

/**
 * Hook for checking if ALL specified capabilities are enabled
 */
export const useRequireCapabilities = (
  organizationId: number | null,
  requiredCapabilities: string[]
) => {
  const { capabilities, loading } = useCapabilities(organizationId, requiredCapabilities);

  const allEnabled = requiredCapabilities.every(cap => capabilities[cap]);

  return { allEnabled, loading };
};

/**
 * Hook for checking if ANY of the specified capabilities are enabled
 */
export const useHasAnyCapability = (
  organizationId: number | null,
  capabilityNames: string[]
) => {
  const { capabilities, loading } = useCapabilities(organizationId, capabilityNames);

  const hasAny = capabilityNames.some(cap => capabilities[cap]);

  return { hasAny, loading };
};

export default useCapabilities;
